import type {
  DataProvider,
  CrudFilter,
  CrudSorting,
  Pagination,
} from '@refinedev/core';
import { axiosInstance } from '@/providers/axiosInstance';

const API_URL = '/api/admin';

function buildQueryParams({
  pagination,
  sorters,
  filters,
}: {
  pagination?: Pagination;
  sorters?: CrudSorting;
  filters?: CrudFilter[];
}): Record<string, string | number> {
  const params: Record<string, string | number> = {};

  if (pagination && pagination.mode !== 'off') {
    const current = pagination.current ?? 1;
    const pageSize = pagination.pageSize ?? 10;
    params._start = (current - 1) * pageSize;
    params._end = current * pageSize;
  }

  if (sorters && sorters.length > 0) {
    const firstSorter = sorters[0];
    if (firstSorter) {
      params._sort = firstSorter.field;
      params._order = firstSorter.order;
    }
  }

  if (filters && filters.length > 0) {
    for (const filter of filters) {
      if ('field' in filter) {
        if (filter.operator === 'eq') {
          params[filter.field] = filter.value as string | number;
        } else if (filter.operator === 'in' && Array.isArray(filter.value)) {
          params[filter.field] = (filter.value as string[]).join(',');
        } else if (filter.operator === 'contains') {
          params.q = filter.value as string;
        }
      }
    }
  }

  return params;
}

export const dataProvider: DataProvider = {
  getApiUrl: () => API_URL,

  getList: async ({ resource, pagination, sorters, filters }) => {
    const params = buildQueryParams({ pagination, sorters, filters });

    const response = await axiosInstance.get(`${API_URL}/${resource}`, {
      params,
    });

    // Some endpoints (e.g. checkpoints) wrap data in { data, total }
    // while others (e.g. orders) return a flat array.
    const responseData = Array.isArray(response.data)
      ? response.data
      : ((response.data?.data as unknown[]) ?? response.data);

    const total = Number(
      response.headers['x-total-count'] ??
        response.data?.total ??
        (Array.isArray(responseData) ? responseData.length : 0)
    );

    return {
      data: responseData,
      total,
    };
  },

  getOne: async ({ resource, id }) => {
    const response = await axiosInstance.get(`${API_URL}/${resource}/${id}`);

    return {
      data: response.data,
    };
  },

  create: async ({ resource, variables }) => {
    const response = await axiosInstance.post(
      `${API_URL}/${resource}`,
      variables
    );

    return {
      data: response.data,
    };
  },

  update: async ({ resource, id, variables }) => {
    const response = await axiosInstance.patch(
      `${API_URL}/${resource}/${id}`,
      variables
    );

    return {
      data: response.data,
    };
  },

  deleteOne: async ({ resource, id }) => {
    const response = await axiosInstance.delete(`${API_URL}/${resource}/${id}`);

    return {
      data: response.data,
    };
  },

  custom: async ({ url, method, payload, query: queryParams, headers }) => {
    const requestConfig = {
      headers: headers as Record<string, string> | undefined,
      params: queryParams as Record<string, string> | undefined,
    };

    let response;
    switch (method) {
      case 'get':
        response = await axiosInstance.get(url, requestConfig);
        break;
      case 'post':
        response = await axiosInstance.post(url, payload, requestConfig);
        break;
      case 'put':
        response = await axiosInstance.put(url, payload, requestConfig);
        break;
      case 'patch':
        response = await axiosInstance.patch(url, payload, requestConfig);
        break;
      case 'delete':
        response = await axiosInstance.delete(url, requestConfig);
        break;
      default:
        response = await axiosInstance.get(url, requestConfig);
    }

    return {
      data: response.data,
    };
  },
};
